/**
 * 62.48 — the bot parameter catalogue (decision 121's third stage: the groundwork for aggressive bot parametrisation).
 * Every bot knob on both clocks in one typed table: its path, today's value, its range and the code that reads it.
 *
 *   bot.skill.<difficulty>, bot.personality.<preset>.<trait>  — setup (both clocks): a difficulty's skill, a preset's traits
 *   turn.tuning.<section>.<key>…                             — the turn bot's base tuning (botParamsTurn.ts)
 *   turn.profile.<level>.<section>.<key>…                    — a skill level's profile over it (a key a profile sets is changed here)
 *   action.<group>.<key>…                                    — the action brains (botParamsAction.ts)
 *
 * A bot-params file (`--bot-params FILE` on sim:bots, sim:duel, sim:skirmish, sim:sector; `MatchSetup.botParams`) is
 * `{ params?: {path: value}, byTeam?: [{path: value} | null, …] }`. Absent paths keep today's value, so an empty file
 * is today's bots (the goldens prove it). Every brain reads its values through `teamBotParams` — the one accessor.
 */
import type { BotParamDef, BotParamSet, BotParamsConfig, BotPersonality } from '../types';
import { BOT_PRESETS, BOT_SKILL_LEVELS } from '../constants';
import { catalogueOf, paramProblem } from './botParamsLib';
import { TURN_PARAMS, TURN_PROFILE_PARAMS } from './botParamsTurn';
import { ACTION_PARAM_DEFS, actionParamsFrom, type ActionParams } from './botParamsAction';

/** The knobs setup reads for both clocks: what skill a difficulty means, and each personality preset's traits. */
export const SHARED_PARAMS: readonly BotParamDef[] = [
  ...catalogueOf('bot.skill', BOT_SKILL_LEVELS, () => 'match/setup (a difficulty → its skill, both clocks)', { '.easy': { min: 0, max: 1 }, '.medium': { min: 0, max: 1 }, '.hard': { min: 0, max: 1 }, '.expert': { min: 0, max: 1 } }),
  ...catalogueOf('bot.personality', BOT_PRESETS, () => 'match/setup (a preset → its traits, both clocks)', { '.riskTolerance': { min: 0, max: 1 } }),
];

/** The whole catalogue, in path order within each group. */
export const BOT_PARAMS: readonly BotParamDef[] = [...SHARED_PARAMS, ...TURN_PARAMS, ...TURN_PROFILE_PARAMS, ...ACTION_PARAM_DEFS];

const BY_PATH = new Map(BOT_PARAMS.map(d => [d.path, d]));

/** The row for a path, or undefined. */
export function botParam(path: string): BotParamDef | undefined {
  return BY_PATH.get(path);
}

/** Every problem with a params file (unknown paths, wrong types, out of range), or an empty list. */
export function botParamsProblems(config: BotParamsConfig | undefined): string[] {
  if (!config) return [];
  const out: string[] = [];
  const check = (set: BotParamSet | null | undefined, where: string) => {
    for (const [path, value] of Object.entries(set ?? {})) {
      const p = paramProblem(BY_PATH.get(path), path, value);
      if (p) out.push(`${where}: ${p}`);
    }
  };
  check(config.params, 'params');
  (config.byTeam ?? []).forEach((s, t) => check(s, `byTeam[${t}]`));
  return out;
}

/** Throws on the first problems (a typo must never run as today's bot silently). */
export function assertBotParams(config: BotParamsConfig | undefined): void {
  const problems = botParamsProblems(config);
  if (problems.length) throw new Error(`bot params: ${problems.slice(0, 5).join('; ')}${problems.length > 5 ? ` (+${problems.length - 5} more)` : ''}`);
}

/** Read a params file's JSON (a bare `{path: value}` object counts as `params`; other keys — an evolve run's history — are ignored). Validated. */
export function parseBotParams(json: string): BotParamsConfig {
  const raw = JSON.parse(json) as Record<string, unknown>;
  const config: BotParamsConfig = 'params' in raw || 'byTeam' in raw
    ? { ...(raw.params ? { params: raw.params as BotParamSet } : {}), ...(raw.byTeam ? { byTeam: raw.byTeam as BotParamsConfig['byTeam'] } : {}) }
    : { params: raw as BotParamSet };
  assertBotParams(config);
  return config;
}

/** The one accessor: a team's values — every team's, with its own on top. Undefined when nothing is set. */
export function teamBotParams(config: BotParamsConfig | undefined, team: number): BotParamSet | undefined {
  if (!config) return undefined;
  const own = config.byTeam?.[team];
  if (!config.params && !own) return undefined;
  return { ...(config.params ?? {}), ...(own ?? {}) };
}

/** A difficulty's skill under a team's values (BOT_SKILL_LEVELS; an unknown difficulty plays medium). */
export function skillFor(difficulty: string, values: BotParamSet | undefined): number {
  const key = difficulty in BOT_SKILL_LEVELS ? difficulty : 'medium';
  const v = values?.[`bot.skill.${key}`];
  return typeof v === 'number' ? v : BOT_SKILL_LEVELS[key];
}

/** A personality preset's traits under a team's values (BOT_PRESETS; an unknown preset is balanced). */
export function personalityFor(preset: string, values: BotParamSet | undefined): BotPersonality {
  const key = preset in BOT_PRESETS ? preset : 'balanced';
  const base = BOT_PRESETS[key];
  if (!values) return base;
  let out = base;
  for (const trait of Object.keys(base) as Array<keyof BotPersonality>) {
    const v = values[`bot.personality.${key}.${trait}`];
    if (typeof v === 'number') out = { ...out, [trait]: v };
  }
  return out;
}

/** A team's action knobs (ACTION_PARAMS when nothing is set). */
export function actionParamsFor(config: BotParamsConfig | undefined, team: number): ActionParams {
  return actionParamsFrom(teamBotParams(config, team));
}

/** Whether teams' action knobs differ (a harness then drives each team with its own bots). */
export function actionParamsDifferByTeam(config: BotParamsConfig | undefined): boolean {
  return !!config?.byTeam?.some(s => s && Object.keys(s).some(k => k.startsWith('action.')));
}

export { withTurnParams } from './botParamsTurn';
export { ACTION_PARAMS, actionParamsFrom, type ActionParams } from './botParamsAction';
